import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, X, Trash2 } from 'lucide-react';
import {
  Action,
  ActionType,
  ACTION_TYPES,
  getActionTypeMetadata,
} from '@/types/action';

interface ActionEditorProps {
  action: Partial<Action>;
  onSave: (action: Partial<Action>) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving?: boolean;
}

export function ActionEditor({
  action,
  onSave,
  onCancel,
  onDelete,
  isSaving = false,
}: ActionEditorProps) {
  const isNewAction = !action.id;
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const [editedAction, setEditedAction] = useState<Partial<Action>>({
    actionType: action.actionType || 'apply_label',
    parameters: action.parameters || {},
    ...action,
  });

  const actionTypeMetadata = getActionTypeMetadata(
    editedAction.actionType as ActionType
  );

  // Focus first field when creating a new action
  useEffect(() => {
    if (isNewAction && firstFieldRef.current && actionTypeMetadata?.parameters.length > 0) {
      firstFieldRef.current.focus();
    }
  }, [isNewAction, actionTypeMetadata]);

  const handleTypeChange = (newType: ActionType) => {
    // Reset parameters when changing type
    const newMetadata = getActionTypeMetadata(newType);
    const newParameters: Record<string, any> = {};

    // Initialize required parameters with empty values
    if (newMetadata) {
      newMetadata.parameters.forEach((param) => {
        if (param.required) {
          newParameters[param.name] = '';
        }
      });
    }

    setEditedAction({
      ...editedAction,
      actionType: newType,
      parameters: newParameters,
    });
  };

  const handleParameterChange = (name: string, value: string) => {
    setEditedAction({
      ...editedAction,
      parameters: {
        ...editedAction.parameters,
        [name]: value,
      },
    });
  };

  const validateParameters = (): boolean => {
    if (!actionTypeMetadata) return false;

    for (const param of actionTypeMetadata.parameters) {
      if (param.required) {
        const value = editedAction.parameters?.[param.name];
        if (!value || value.trim() === '') {
          return false;
        }

        // Email validation
        if (param.type === 'email') {
          const email = value.trim();
          if (email.includes(' ')) {
            return false;
          }
          if (!email.includes('@') || !email.includes('.')) {
            return false;
          }
        }
      }
    }

    // Ensure no-param actions have empty parameters
    if (actionTypeMetadata.parameters.length === 0) {
      return Object.keys(editedAction.parameters || {}).length === 0;
    }

    return true;
  };

  const handleSave = () => {
    if (validateParameters()) {
      onSave(editedAction);
    }
  };

  const isValid = validateParameters();

  return (
    <Card className="bg-slate-50 dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 overflow-hidden rounded-lg">
      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Action Type */}
        <div className="py-4">
          <Select
            value={editedAction.actionType}
            onValueChange={handleTypeChange}
          >
            <SelectTrigger id="action-type" className="bg-white dark:bg-gray-900">
              <SelectValue placeholder="Select action type" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_TYPES.map((at) => (
                <SelectItem key={at.value} value={at.value}>
                  {at.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Parameter Inputs */}
        {actionTypeMetadata && actionTypeMetadata.parameters.length > 0 && (
          <div className="space-y-4">
            {actionTypeMetadata.parameters.map((param, index) => {
              const value = editedAction.parameters?.[param.name] || '';

              return (
                <div key={param.name} className="space-y-3">
                  <Label htmlFor={param.name}>
                    {param.name === 'label_name'
                      ? 'Label Name'
                      : param.name === 'sender_email'
                      ? 'Sender Email'
                      : param.name}
                    {param.required && <span className="text-red-500"> *</span>}
                  </Label>
                  <Input
                    ref={index === 0 ? firstFieldRef : null}
                    id={param.name}
                    type={param.type === 'email' ? 'email' : 'text'}
                    value={value}
                    onChange={(e) =>
                      handleParameterChange(param.name, e.target.value)
                    }
                    placeholder={param.placeholder}
                  />
                  {param.placeholder?.includes('{{') && (
                    <p className="text-xs text-slate-400">
                      You can use template variables like {param.placeholder}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Container - Action Buttons */}
      <div className="p-6 bg-slate-100 dark:bg-slate-800 border-t border-gray-300 dark:border-gray-600 flex gap-2 justify-between">
        {onDelete && action.id && (
          <Button
            type="button"
            variant="ghost"
            onClick={onDelete}
            disabled={isSaving}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          <Button
            onClick={handleSave}
            disabled={isSaving || !isValid}
          >
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save Action'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
